import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { SERVICE_OPTIONS } from "../../data/site";
import { trackQuoteLead } from "../../utils/metaPixel";

const inputClass =
  "w-full rounded-xl border border-white/10 bg-charcoal px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-amber focus:ring-1 focus:ring-amber";

export const QuoteForm = ({ defaultService = "General Enquiry" }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: defaultService,
    budget: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const submissionLocked = useRef(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email address.";
    if (form.message.trim().length < 5) errs.message = "Tell us a little about your project.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submissionLocked.current || !validate()) return;
    submissionLocked.current = true;
    setSubmitting(true);
    try {
      const response = await api.post("/quotes", form);
      if (![200, 201].includes(response.status) || typeof response.data?.id !== "string" || !response.data.id.trim()) {
        throw new Error("The server did not confirm your enquiry. Please try again.");
      }
      setDone(true);
      trackQuoteLead(response.data.id);
      toast.success("Quote request received — we'll be in touch shortly.");
    } catch (err) {
      submissionLocked.current = false;
      toast.error(err.response ? formatApiErrorDetail(err.response.data?.detail) : "Could not confirm your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quote-form-wrapper" className="relative">
      <AnimatePresence mode="wait">
        {done ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center rounded-2xl border border-amber/30 bg-charcoal p-10 text-center"
            data-testid="quote-success"
          >
            <CheckCircle2 className="text-amber" size={52} />
            <h3 className="mt-4 font-display text-2xl font-bold uppercase text-white">Request Received</h3>
            <p className="mt-2 max-w-md text-sm text-white/60">
              Thanks {form.name.split(" ")[0]}! Your enquiry is logged. Our team will review it and get back to you.
              We typically respond within 2 business hours.
            </p>
            <button
              data-testid="quote-reset-btn"
              onClick={() => {
                submissionLocked.current = false;
                setDone(false);
                setForm({ name: "", email: "", phone: "", service: defaultService, budget: "", message: "" });
              }}
              className="mt-6 rounded-full border border-white/15 px-6 py-2.5 font-mono text-xs uppercase tracking-widest text-white/80 transition-colors hover:border-amber hover:text-amber"
            >
              Send Another
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={submit}
            data-testid="quote-form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4 rounded-2xl border border-white/10 bg-obsidian p-6 sm:p-8"
            noValidate
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-white/50" htmlFor="q-name">
                  Name *
                </label>
                <input id="q-name" data-testid="quote-name-input" className={inputClass} value={form.name} onChange={set("name")} placeholder="Jane Murphy" />
                {errors.name && <p data-testid="error-name" className="mt-1 text-xs text-red-400">{errors.name}</p>}
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-white/50" htmlFor="q-email">
                  Email *
                </label>
                <input id="q-email" data-testid="quote-email-input" className={inputClass} value={form.email} onChange={set("email")} placeholder="you@business.ie" />
                {errors.email && <p data-testid="error-email" className="mt-1 text-xs text-red-400">{errors.email}</p>}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-white/50" htmlFor="q-phone">
                  Phone
                </label>
                <input id="q-phone" data-testid="quote-phone-input" className={inputClass} value={form.phone} onChange={set("phone")} placeholder="Optional" />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-white/50" htmlFor="q-budget">
                  Budget
                </label>
                <input id="q-budget" data-testid="quote-budget-input" className={inputClass} value={form.budget} onChange={set("budget")} placeholder="Optional, e.g. €5k–€10k" />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-white/50" htmlFor="q-service">
                Service
              </label>
              <select id="q-service" data-testid="quote-service-select" className={inputClass} value={form.service} onChange={set("service")}>
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s} className="bg-charcoal">
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-white/50" htmlFor="q-message">
                Project Details *
              </label>
              <textarea id="q-message" data-testid="quote-message-input" rows={4} className={inputClass} value={form.message} onChange={set("message")} placeholder="Tell us about your shopfront, signage or branding project…" />
              {errors.message && <p data-testid="error-message" className="mt-1 text-xs text-red-400">{errors.message}</p>}
            </div>

            <button
              type="submit"
              data-testid="quote-form-submit-btn"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-amber py-3.5 font-display text-sm font-bold uppercase tracking-wide text-void transition-transform hover:scale-[1.01] hover:bg-amber-glow disabled:opacity-60"
            >
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={16} />}
              {submitting ? "Sending…" : "Request My Free Quote"}
            </button>
            <p className="text-center font-mono text-[10px] uppercase tracking-wider text-white/30">
              No spam · Your details are stored securely
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
